import { useQuery, useQueryClient } from "@tanstack/react-query";
import { OptionSelect } from "Common/Components";
import { useSessionStore } from "Common/Stores";
import { useFormik } from "formik";
import {
  validateSelect,
  validateYupStringWithSpecialCharacters,
} from "helpers/functionsHelpers";
import { NotificationType, showNotifications } from "helpers/toas_notification";
import { ProductTransferPost, warehouseListI } from "interfaces";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createTransfer, getWarehouse, getWarehouseByBranch } from "services";
import * as Yup from "yup";
import { useInvAdjustmentStore } from "../stores/storeInventoryAdjustment";
import { InventoryAdjustment, PostTransfers } from "interfaces/InventoryAdjustment";
import { postInventoryAdjustment } from '../../../../services/service.cruds/InvetoryAdjustment/serviceInvetoryAdjustment';

export const useInvAdjustmentForm = () => {
  const { t } = useTranslation();

  const { session } = useSessionStore();

  const queryClient = useQueryClient();

  const {
    setPayloadTransfer,
    clearAllStore,
    payloadTransfer,
    productsTransfer,
  } = useInvAdjustmentStore();


  const branchRef = useRef<string>(String(session?.employe.branch.id || ""));

  const existProductsInStore = useMemo(() => {
    return productsTransfer.length > 0;
  }, [productsTransfer]);
  

  const initialValues = {
    type: "",
    observations: "",
    warehouseId: "",
  };

  const validationSchema = Yup.object().shape({
    observations: validateYupStringWithSpecialCharacters(t, true),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!existProductsInStore) {
        showNotifications(
          NotificationType.ERROR,
          t("transferBetweenWarehouse.productStoreEmpty")
        );
        return;
      }

      const products: ProductTransferPost[] = productsTransfer.map((item) => {
        return {
          productCode: item.product?.productCode.productCode || "",
          quantity: item.quantity,
        } as ProductTransferPost;
      });

      const payload: PostTransfers = {
        warehouseId: Number(values.warehouseId),
        type: values.type,
        observation: values.observations,
        productAjust : products,
      };

      try {
        const respTransfer = await postInventoryAdjustment(payload);

        if (respTransfer.code === "200") {
          showNotifications(
            NotificationType.SUCCESS,
            t("InventoryAdjustment.transsave")
          );

          formik.resetForm();
          clearAllStore();
          queryClient.invalidateQueries({
            queryKey: ["productsByWarehouse"],
          });
        } else {
          showNotifications(
            NotificationType.SUCCESS,
            t("InventoryAdjustment.transsave")
          );
        }
      } catch (error) {
        showNotifications(
          NotificationType.ERROR,
          t("transferBetweenWarehouse.transferError") + " " + error
        );
      }
    },
  });

  function createRows(data: warehouseListI[]) {
    return data.map((item) => {
      return {
        label: item.description,
        value: item.id.toString(),
      };
    });
  }
  
  const { data: warehousesData } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => getWarehouseByBranch(branchRef.current),
    enabled: !!branchRef.current,
  });
  

  const warehousesOptions = useMemo(() => {
    if (warehousesData) {
      return createRows(warehousesData); 
    }
    return [];
  }, [warehousesData]);
  return {
    t,
    warehousesData: warehousesOptions,
    formik,
    payloadTransfer,
    existProductsInStore,
    setPayloadTransfer,
  };
};
